import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, ShieldCheck, MessageCircleHeart, BadgeCheck, ArrowRight, Menu, X, Quote, Scale, Feather } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } },
};

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/23654f4e-5b06-4530-8391-36167697d45a/images/6574179a0df148fcef8054f060e32181948c2dbb60974da92d5562076bbc90da.png";
const CONNECTION_IMG = "https://static.prod-images.emergentagent.com/jobs/23654f4e-5b06-4530-8391-36167697d45a/images/e8523ae5eef8415d97a6df2fd672f8b6a785a1b4738ce2efdc937e2b81c942e2.png";
const TESTIMONIAL_IMG = "https://images.pexels.com/photos/16683257/pexels-photo-16683257.jpeg";

export default function Landing() {
  const [waitlistEmail, setWaitlistEmail] = useState("");
  const [waitlistName, setWaitlistName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const handleWaitlist = async (e) => {
    e.preventDefault();
    if (!waitlistEmail) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/waitlist", { email: waitlistEmail, name: waitlistName });
      toast.success(data.already_joined ? "You're already on the list." : "Welcome to Wanted. You're on the list.");
      setWaitlistEmail("");
      setWaitlistName("");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen text-[#F8F8F8] font-body overflow-x-hidden" data-testid="landing-page">
      {/* NAV */}
      <nav className="fixed top-0 inset-x-0 z-50 glass-strong" data-testid="main-nav">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
            <span className="font-display text-2xl tracking-tight text-gold">Wanted</span>
            <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]" />
          </Link>
          <div className="hidden md:flex items-center gap-10 text-sm text-white/70">
            <a href="#about" className="hover:text-white transition-colors" data-testid="nav-about">About</a>
            <a href="#why" className="hover:text-white transition-colors" data-testid="nav-why">Why Wanted</a>
            <a href="#equal" className="hover:text-white transition-colors" data-testid="nav-equal">Membership</a>
            <a href="#testimonials" className="hover:text-white transition-colors" data-testid="nav-testimonials">Feeling</a>
            <Link to="/login" className="hover:text-white transition-colors" data-testid="nav-signin">Sign in</Link>
          </div>
          <div className="hidden md:block">
            <a href="#waitlist" className="btn-gold" data-testid="nav-join-waitlist">Join Waitlist</a>
          </div>
          <button className="md:hidden text-white" onClick={() => setMenuOpen(!menuOpen)} data-testid="mobile-menu-toggle">
            {menuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
        {menuOpen && (
          <div className="md:hidden border-t border-white/10 bg-[#050505]/95 px-6 py-6 space-y-4">
            <a href="#about" onClick={() => setMenuOpen(false)} className="block text-white/80">About</a>
            <a href="#why" onClick={() => setMenuOpen(false)} className="block text-white/80">Why Wanted</a>
            <a href="#equal" onClick={() => setMenuOpen(false)} className="block text-white/80">Membership</a>
            <a href="#testimonials" onClick={() => setMenuOpen(false)} className="block text-white/80">Feeling</a>
            <Link to="/login" onClick={() => setMenuOpen(false)} className="block text-white/80">Sign in</Link>
            <a href="#waitlist" onClick={() => setMenuOpen(false)} className="btn-gold inline-block">Join Waitlist</a>
          </div>
        )}
      </nav>

      {/* HERO */}
      <section className="relative min-h-screen flex items-center pt-20" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/70 via-[#050505]/50 to-[#050505]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-32 grid lg:grid-cols-12 gap-12 items-center">
          <motion.div
            initial="hidden"
            animate="show"
            variants={{ show: { transition: { staggerChildren: 0.12 } } }}
            className="lg:col-span-8"
          >
            <motion.div variants={fadeUp} className="flex items-center gap-3 mb-8">
              <span className="h-px w-12 bg-gold" />
              <span className="text-gold-soft tracking-overline text-xs">A NEW WAY TO BE WANTED</span>
            </motion.div>

            <motion.h1
              variants={fadeUp}
              className="font-display font-light text-5xl sm:text-6xl lg:text-7xl xl:text-8xl leading-[0.95] tracking-tight"
              data-testid="hero-headline"
            >
              Because everyone <br className="hidden sm:block" />
              deserves to feel <em className="not-italic shimmer-text font-normal">wanted</em>.
            </motion.h1>

            <motion.p variants={fadeUp} className="mt-10 text-lg sm:text-xl text-white/65 max-w-2xl leading-relaxed font-light">
              A members-only dating experience for people who want to feel chosen — not consumed.
              Verified profiles. Real conversations. No endless swiping.
            </motion.p>

            <motion.div variants={fadeUp} className="mt-12 flex flex-col sm:flex-row gap-4">
              <a href="#waitlist" className="btn-gold inline-flex items-center justify-center gap-2" data-testid="hero-join-waitlist">
                Join the Waitlist <ArrowRight size={16} />
              </a>
              <Link to="/register" className="btn-outline-gold inline-flex items-center justify-center" data-testid="hero-create-account">
                Create account
              </Link>
            </motion.div>

            <motion.div variants={fadeUp} className="mt-16 flex flex-wrap items-center gap-x-6 gap-y-3 text-xs text-white/50 tracking-overline">
              <span className="flex items-center gap-2"><BadgeCheck size={14} className="text-gold" /> VERIFIED MEMBERS ONLY</span>
              <span className="w-1 h-1 rounded-full bg-white/30" />
              <span>MEMBERS-ONLY</span>
              <span className="w-1 h-1 rounded-full bg-white/30" />
              <span>NO ENDLESS SWIPING</span>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.4 }}
            className="lg:col-span-4 hidden lg:block"
          >
            <div className="glass-strong rounded-3xl p-10 grain">
              <div className="text-gold-soft tracking-overline text-xs mb-7">DESIGNED FOR THE INTENTIONAL</div>
              <p className="font-display text-2xl leading-snug font-light text-white/95 mb-7">
                "Feel chosen, not consumed."
              </p>
              <div className="h-px w-12 bg-[#D4AF37]/50 mb-7" />
              <div className="space-y-5 text-sm text-white/65 font-light leading-relaxed">
                <p>Every member is real. Every member is verified. Every member is here on purpose.</p>
                <p className="text-white/85">This is how dating was supposed to feel.</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ABOUT */}
      <section id="about" className="py-32 lg:py-40 relative" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9 }}
            className="lg:col-span-5"
          >
            <div className="aspect-[4/5] rounded-3xl overflow-hidden">
              <img src={CONNECTION_IMG} alt="" className="w-full h-full object-cover" />
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9 }}
            className="lg:col-span-7"
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-12 bg-gold" />
              <span className="text-gold-soft tracking-overline text-xs">ABOUT WANTED</span>
            </div>
            <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              We built the app we wished existed when we were tired of swiping.
            </h2>
            <p className="mt-8 text-white/65 text-lg leading-relaxed font-light max-w-xl">
              Wanted is for the people who want to be chosen — not just matched. Slower. More thoughtful.
              Unapologetically intentional. No infinite feeds. No superficial metrics.
              Just real, verified people, looking for real connection.
            </p>

            <div className="mt-12 grid sm:grid-cols-3 gap-6">
              {[
                { icon: <Feather size={18} />, t: "Intentional", d: "Curated daily, not infinite." },
                { icon: <BadgeCheck size={18} />, t: "Verified", d: "Every member is a real person." },
                { icon: <Scale size={18} />, t: "Equally invested", d: "Everyone subscribes. The same way." },
              ].map((it) => (
                <div key={it.t} className="border-l border-[#D4AF37]/30 pl-5">
                  <div className="w-9 h-9 rounded-full border border-[#D4AF37]/40 flex items-center justify-center text-gold mb-3">
                    {it.icon}
                  </div>
                  <div className="font-display text-xl text-white mb-1">{it.t}</div>
                  <div className="text-sm text-white/55 font-light leading-relaxed">{it.d}</div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* WHY DIFFERENT */}
      <section id="why" className="py-32 lg:py-40 relative" data-testid="why-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mb-20">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-12 bg-gold" />
              <span className="text-gold-soft tracking-overline text-xs">WHY WANTED IS DIFFERENT</span>
            </div>
            <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              Real connection. <em className="not-italic text-gold">Not endless swiping.</em>
            </h2>
            <p className="mt-6 text-white/55 text-lg font-light leading-relaxed max-w-2xl">
              We built Wanted on a simple belief: the way most dating apps work has made us all
              feel a little less human. We're trying something quieter — and warmer.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: <Heart size={20} />, t: "Curated, not consumed", d: "A handful of thoughtful matches a day — not an endless deck of strangers." },
              { icon: <BadgeCheck size={20} />, t: "Verified humans only", d: "Every member completes a selfie verification. No bots. No catfish. No second-guessing." },
              { icon: <MessageCircleHeart size={20} />, t: "Conversations that matter", d: "Real-time messaging built for slow, meaningful exchanges — not one-liners and ghosting." },
              { icon: <ShieldCheck size={20} />, t: "Emotionally safe", d: "A space designed to feel warm, soft, and unhurried — because care matters." },
              { icon: <Scale size={20} />, t: "Equal commitment", d: "Both members of every match are paying members. No asymmetry. No transactional feel." },
              { icon: <Feather size={20} />, t: "Privacy by design", d: "Your photos, conversations, and data belong to you. Always." },
            ].map((f, i) => (
              <motion.div
                key={f.t}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.06 }}
                className="glass rounded-3xl p-10 hover:border-[#D4AF37]/30 transition-all duration-300 hover:-translate-y-1"
                data-testid={`why-card-${i}`}
              >
                <div className="w-12 h-12 rounded-full border border-[#D4AF37]/40 flex items-center justify-center text-gold mb-6">
                  {f.icon}
                </div>
                <h3 className="font-display text-2xl text-white mb-3">{f.t}</h3>
                <p className="text-white/55 leading-relaxed font-light">{f.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* EQUAL COMMITMENT — Membership positioning */}
      <section id="equal" className="py-32 lg:py-40 relative" data-testid="equal-section">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 text-center">
          <div className="inline-flex items-center gap-3 mb-8">
            <span className="h-px w-12 bg-gold" />
            <span className="text-gold-soft tracking-overline text-xs">EQUAL COMMITMENT</span>
            <span className="h-px w-12 bg-gold" />
          </div>
          <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
            Both members invested. <em className="not-italic text-gold">Equally.</em>
          </h2>
          <p className="mt-8 text-white/65 text-lg leading-relaxed font-light max-w-2xl mx-auto">
            On Wanted, every member — regardless of who they are or who they're looking for —
            pays the same: <span className="text-white font-medium">$24.99 a month</span>.
            No "free for some, paid for others." That asymmetry is what makes dating feel transactional.
            We don't do that here.
          </p>

          <div className="mt-16 grid md:grid-cols-3 gap-6 text-left">
            {[
              { t: "One price, one promise", d: "$24.99/month for everyone. The same membership, the same commitment." },
              { t: "Mutual investment", d: "When both people pay, both people show up. That's how care begins." },
              { t: "Not for the hookup crowd", d: "We're not for everyone. We're for the people taking this seriously." },
            ].map((it) => (
              <div key={it.t} className="glass rounded-3xl p-8">
                <div className="font-display text-xl text-white mb-3">{it.t}</div>
                <p className="text-white/55 leading-relaxed font-light">{it.d}</p>
              </div>
            ))}
          </div>

          <div className="mt-12">
            <a href="#waitlist" className="btn-outline-gold inline-flex items-center gap-2" data-testid="equal-cta">
              Reserve your invitation <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </section>

      {/* WHAT PEOPLE WANT DATING TO FEEL LIKE */}
      <section id="testimonials" className="py-32 lg:py-40 relative" data-testid="testimonials-section">
        <div className="absolute inset-0 opacity-30">
          <img src={TESTIMONIAL_IMG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-[#050505]/80 to-[#050505]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mb-20">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-12 bg-gold" />
              <span className="text-gold-soft tracking-overline text-xs">THE FEELING WE'RE BUILDING</span>
            </div>
            <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              What people want dating to <em className="not-italic text-gold">feel like</em>.
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              "I want connection, not endless swiping.",
              "I want to feel chosen, not consumed.",
              "I want conversations that actually matter.",
              "I want dating to feel human again.",
            ].map((q, i) => (
              <motion.div
                key={q}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                className="glass-strong rounded-3xl p-10 flex flex-col justify-between min-h-[260px] hover:border-[#D4AF37]/30 transition-all duration-500 hover:-translate-y-1"
                data-testid={`feeling-card-${i}`}
              >
                <Quote className="text-gold mb-6" size={24} />
                <p className="font-display text-2xl lg:text-[26px] text-white/95 leading-snug italic font-light">
                  "{q}"
                </p>
                <div className="mt-8 h-px w-10 bg-[#D4AF37]/40" />
              </motion.div>
            ))}
          </div>
          <div className="mt-16 text-center max-w-xl mx-auto">
            <p className="text-white/55 font-light leading-relaxed italic">
              These aren't testimonials. They're the quiet wishes we've heard
              from everyone tired of dating apps — and the standard we're building Wanted to meet.
            </p>
          </div>
        </div>
      </section>

      {/* WAITLIST */}
      <section id="waitlist" className="py-32 lg:py-40 relative" data-testid="waitlist-section">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 text-center">
          <div className="inline-flex items-center gap-3 mb-8">
            <span className="h-px w-12 bg-gold" />
            <span className="text-gold-soft tracking-overline text-xs">JOIN THE WAITLIST</span>
            <span className="h-px w-12 bg-gold" />
          </div>
          <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
            Be one of the first to feel <em className="not-italic text-gold">wanted</em>.
          </h2>
          <p className="mt-8 text-white/60 text-lg leading-relaxed font-light">
            We open invitations slowly, one quiet wave at a time.
            Leave your details — we'll let you know when it's your turn.
          </p>

          <form onSubmit={handleWaitlist} className="mt-12 max-w-md mx-auto space-y-4" data-testid="waitlist-form">
            <input
              type="text"
              placeholder="Your name (optional)"
              value={waitlistName}
              onChange={(e) => setWaitlistName(e.target.value)}
              className="input-luxe text-center"
              data-testid="waitlist-name-input"
            />
            <input
              type="email"
              required
              placeholder="your@email.com"
              value={waitlistEmail}
              onChange={(e) => setWaitlistEmail(e.target.value)}
              className="input-luxe text-center"
              data-testid="waitlist-email-input"
            />
            <button type="submit" disabled={submitting} className="btn-gold w-full" data-testid="waitlist-submit-btn">
              {submitting ? "Joining..." : "Reserve my invitation"}
            </button>
          </form>
          <p className="mt-6 text-xs text-white/40 tracking-wide">
            No spam. Just one warm email when it's your time.
          </p>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-white/5 py-16" data-testid="footer">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid md:grid-cols-5 gap-12">
          <div className="md:col-span-2">
            <div className="font-display text-3xl tracking-tight text-gold mb-4">Wanted</div>
            <p className="text-white/50 leading-relaxed font-light max-w-sm">
              Because everyone deserves to feel wanted. A new chapter of dating, designed with care.
            </p>
            <p className="text-xs text-white/30 tracking-overline mt-8">WWW.JOINWANTED.COM</p>
          </div>
          <div>
            <div className="text-xs tracking-overline text-white/40 mb-4">EXPLORE</div>
            <ul className="space-y-3 text-sm text-white/70 font-light">
              <li><a href="#about" className="hover:text-gold transition-colors">About</a></li>
              <li><a href="#why" className="hover:text-gold transition-colors">Why Wanted</a></li>
              <li><a href="#equal" className="hover:text-gold transition-colors">Membership</a></li>
              <li><a href="#testimonials" className="hover:text-gold transition-colors">Feeling</a></li>
              <li><a href="#waitlist" className="hover:text-gold transition-colors">Waitlist</a></li>
            </ul>
          </div>
          <div>
            <div className="text-xs tracking-overline text-white/40 mb-4">ACCOUNT</div>
            <ul className="space-y-3 text-sm text-white/70 font-light">
              <li><Link to="/login" className="hover:text-gold transition-colors">Sign in</Link></li>
              <li><Link to="/register" className="hover:text-gold transition-colors">Create account</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-xs tracking-overline text-white/40 mb-4">LEGAL</div>
            <ul className="space-y-3 text-sm text-white/70 font-light">
              <li><Link to="/terms" className="hover:text-gold transition-colors" data-testid="footer-terms">Terms of Service</Link></li>
              <li><Link to="/privacy" className="hover:text-gold transition-colors" data-testid="footer-privacy">Privacy Policy</Link></li>
              <li><Link to="/refunds" className="hover:text-gold transition-colors" data-testid="footer-refunds">Refund &amp; Cancellation</Link></li>
              <li><Link to="/community-guidelines" className="hover:text-gold transition-colors" data-testid="footer-community">Community Guidelines</Link></li>
            </ul>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 mt-16 pt-8 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="text-xs text-white/30">© {new Date().getFullYear()} Wanted. All rights reserved.</div>
          <div className="text-xs text-white/30 tracking-overline">CRAFTED WITH CARE</div>
        </div>
      </footer>
    </div>
  );
}
